import { forwardRef, useMemo } from 'react';
import styled from '@emotion/styled';
import { type RootNode } from '@trinity/chat/types/rich-text';
import { Lightbulb } from '@trinity/chat-components/components/Chat.Widget/Lightbulb.js';
import { BasicRichText } from '../BasicRichText/BasicRichText';

type Props = {
  title: string;
  backgroundColor: string;
  values: string[];
  labels: string[];
  insights?: string;
};

export const KPIWidget = forwardRef<HTMLDivElement, Props>(({
  title,
  backgroundColor,
  values,
  labels,
  insights,
}, ref) => {
  const getGridLayout = (count: number): string => {
    switch (count) {
      case 1:
        return '1fr';
      case 2:
      case 3:
        return `repeat(${count}, 1fr)`;
      case 4:
        return 'repeat(2, 1fr)';
      case 5:
        return 'repeat(3, 1fr)';
      case 6:
        return 'repeat(3, 1fr)';
      default:
        return '1fr';
    }
  };

  const getGridRows = (count: number): string => {
    switch (count) {
      case 1:
      case 2:
      case 3:
        return '1fr';
      case 4:
      case 6:
        return 'repeat(2, 1fr)';
      case 5:
        return 'auto auto';
      default:
        return '1fr';
    }
  };

  const validValues = values.slice(0, 6); // Ensure max 6 values
  const validLabels = labels.slice(0, 6); // Ensure max 6 labels

  const gridColumns = getGridLayout(validValues.length);
  const gridRows = getGridRows(validValues.length);

  // Memoized insights parsing - only recalculates when insights prop changes
  const renderedInsights = useMemo(() => {
    if (!insights) return null;

    try {
      const parsedInsights = JSON.parse(insights) as RootNode;
      return <BasicRichText value={parsedInsights} />;
    } catch {
      return null;
    }
  }, [insights]);

  return (
    <KPIContainer ref={ref} backgroundColor={backgroundColor}>
      <KPIHeader>
        <KPITitle>{title}</KPITitle>
      </KPIHeader>

      <KPIValuesGrid
        count={validValues.length}
        gridColumns={gridColumns}
        gridRows={gridRows}>
        {validValues.map((value, index) => {
          const label = validLabels[index] || `KPI ${index + 1}`;

          // Handle special layout for 5 values - first 3 in first row, last 2 in second row
          const gridColumn = validValues.length === 5 && index >= 3
            ? `${index - 2} / ${index - 1}`
            : undefined;

          return (
            <KPIValueItem key={index} gridColumn={gridColumn}>
              <KPIValueNumber>{value}</KPIValueNumber>
              <KPIValueLabel>{label}</KPIValueLabel>
            </KPIValueItem>
          );
        })}
      </KPIValuesGrid>

      {insights && <KPIInsights><Lightbulb fill={'#4c4c4c'} size={12} />{renderedInsights}</KPIInsights>}
    </KPIContainer>
  );
});

KPIWidget.displayName = 'KPIWidget';

const KPIContainer = styled.div<{ backgroundColor: string }>(({ backgroundColor, theme }) => ({
  backgroundColor: backgroundColor || '#ffffff',
  containerType: 'inline-size',
  color: theme.palette.text.primary,
  borderRadius: '1.125em',
  border: `1px solid ${theme.palette.divider}`,
  padding: '1.25em',
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1em',
  boxSizing: 'border-box',
}));

const KPIHeader = styled.div({
  textAlign: 'left',
});

const KPITitle = styled.h3(({ theme }) => ({
  margin: 0,
  fontSize: '1em',
  fontWeight: 700,
  color: theme.palette.text.primary,
  // Add your container query here
  '@container (max-width: 200px)': {
    fontSize: '0.80em',
  },
}));

const KPIValuesGrid = styled.div<{
  count: number;
  gridColumns: string;
  gridRows: string;
}>(({ count, gridColumns, gridRows }) => ({
  display: 'grid',
  gridTemplateColumns: gridColumns,
  gridTemplateRows: gridRows,
  gap: '1em',
  flex: 1,
  alignItems: 'center',
  overflowX: 'auto',
  ...(count === 1 && {
    justifyContent: 'center',
    alignContent: 'center',
  }),
  '@container (max-width: 600px)': {
    ...(count === 1 && {
      gridTemplateColumns: '1fr',
    }),
  },
  '@container (max-width: 200px)': {
    gap: '0.5em',
    ...(count !== 1 && {
      gridTemplateColumns: '1fr 1fr',
    }),
    gridTemplateRows: 'auto',
  },
  '@container (max-width: 140px)': {
    gap: '0.5em',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto',
  },
}));

const KPIValueItem = styled.div<{ gridColumn?: string }>(({ gridColumn }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  minHeight: '5em',
  width: '100%',
  ...(gridColumn && {
    gridColumn,
  }),
}));

const KPIValueNumber = styled.div(({ theme }) => ({
  fontSize: '1.75em',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '0.25em',
  lineHeight: 1.2,
  '@container (max-width: 200px)': {
    fontSize: '1.25em',
  },
}));

const KPIValueLabel = styled.div(({ theme }) => ({
  fontSize: '0.75em',
  color: theme.palette.text.primary,
  fontWeight: 500,
  textAlign: 'center',
  wordBreak: 'break-word',
  textWrap: 'balance',
  '@container (max-width: 200px)': {
    fontSize: '0.6em',
  },
}));

const KPIInsights = styled.div(() => ({
  display: 'grid',
  gap: '0.5em',
  gridTemplateColumns: 'auto 1fr',
  '@container (max-width: 400px)': {
    fontSize: '0.80em',
    'p': {
      fontSize: '0.80em',
    },
    'strong': { fontSize: '0.80em' },
    'em': { fontSize: '0.80em' },
    'strong em': { fontSize: '0.80em' },
    '> svg': {
      width: '10px',

    },
  },
}));